import { createFileRoute } from '@tanstack/react-router';
import { Layout } from './-components/layout';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { AnimatedTotalRaised } from './-components/animated-total-raised';
import { useTranslation } from '@/lib/i18n';
import { Suspense, useState, useEffect } from 'react';
import { useQuery, useSuspenseQuery, queryOptions } from '@tanstack/react-query';

export const Route = createFileRoute('/www/demo-animated-counter')({
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex, nofollow' }],
	}),
	component: Component,
});

type Donation = { amountCents: number; timestamp: number };
type WindowResponse = { totalAtWindowStartCents: number; donations: Donation[] };

// ---------------------------------------------------------------------------
// Demo config
// ---------------------------------------------------------------------------

/**
 * In production the key changes every 5 minutes (10:00, 10:05…) but the
 * server returns 6 minutes of data → 1 min margin while the next batch loads.
 * Here we use 8s keys with ~9.6s of data to simulate the same overlap.
 */
const DEMO_WINDOW_MS = 8_000;

/** Simulated historical total in cents */
const BASE_TOTAL_CENTS = 1_523_456_789;

// ---------------------------------------------------------------------------
// Seeded RNG + donation builder
// ---------------------------------------------------------------------------

/** Mulberry32 — same seed → same sequence across browsers */
function mulberry32(seed: number) {
	let s = seed | 0;
	return () => {
		s = (s + 0x6d2b79f5) | 0;
		let t = Math.imul(s ^ (s >>> 15), 1 | s);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

/** Random donation amount between 100 and 10000 cents (€1 – €100) */
function randomAmount(rng: () => number): number {
	return Math.round(rng() * 9900) + 100;
}

/**
 * Build donations for a window. Returns ~9.6s of data for an 8s window
 * (simulates 6 min of data for a 5 min key). The extra ~1.6s past the
 * window boundary provides the overlap margin.
 */
function buildDonations(windowStart: number, rng: () => number): Donation[] {
	return [
		// Burst 1
		{ amountCents: randomAmount(rng), timestamp: windowStart + 800 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 1_100 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 1_400 },
		// Quiet ~1.5s
		// Burst 2
		{ amountCents: randomAmount(rng), timestamp: windowStart + 2_900 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 3_100 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 3_300 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 3_500 },
		// Quiet ~2s
		// Burst 3
		{ amountCents: randomAmount(rng), timestamp: windowStart + 5_500 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 5_800 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 6_100 },
		// Quiet ~1.5s — overlap zone (past the 8s window boundary)
		{ amountCents: randomAmount(rng), timestamp: windowStart + 8_500 },
		{ amountCents: randomAmount(rng), timestamp: windowStart + 9_200 },
	];
}

// ---------------------------------------------------------------------------
// Fake endpoint — mirrors the real getRecentDonationsForAnimation
// ---------------------------------------------------------------------------

/**
 * Simulates the server: given an absolute window start (ms),
 * returns { totalAtWindowStartCents, donations }.
 *
 * Uses the start of the current hour as an implicit epoch to compute
 * totalAtWindowStartCents deterministically (sum of all in-window donations
 * from previous 8s windows since the hour started).
 */
function fakeGetRecentDonations(windowStartMs: number): WindowResponse {
	const hourStart = Math.floor(windowStartMs / (60 * 60_000)) * (60 * 60_000);
	const windowIndex = Math.round((windowStartMs - hourStart) / DEMO_WINDOW_MS);

	// Compute totalAtWindowStartCents by summing previous windows' in-window donations
	let totalAtWindowStartCents = BASE_TOTAL_CENTS;
	for (let i = 0; i < windowIndex; i++) {
		const ws = hourStart + i * DEMO_WINDOW_MS;
		const rng = mulberry32(ws);
		const batch = buildDonations(ws, rng);
		const windowEnd = ws + DEMO_WINDOW_MS;
		// Only count donations within the window boundary (not the overlap)
		totalAtWindowStartCents += batch.filter((d) => d.timestamp < windowEnd).reduce((sum, d) => sum + d.amountCents, 0);
	}

	const rng = mulberry32(windowStartMs);
	const donations = buildDonations(windowStartMs, rng);

	return { totalAtWindowStartCents, donations };
}

// ---------------------------------------------------------------------------
// TanStack Query — mirrors the real recentDonationsOptions
// ---------------------------------------------------------------------------

/** Returns the current absolute 8s window boundary (like currentWindowKey in production) */
function currentDemoWindowKey() {
	return Math.floor(Date.now() / DEMO_WINDOW_MS) * DEMO_WINDOW_MS;
}

const demoRecentDonationsOptions = (windowStartMs: number) =>
	queryOptions({
		queryKey: ['demo-recent-donations', windowStartMs],
		queryFn: () => Promise.resolve(fakeGetRecentDonations(windowStartMs)),
		staleTime: 0,
	});

// ---------------------------------------------------------------------------
// DonationProgressDots
// ---------------------------------------------------------------------------

function DonationProgressDots({ donations }: { donations: Donation[] }) {
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		const allDone = donations.every((d) => d.timestamp <= now);
		if (allDone) return;
		const timer = setInterval(() => setNow(Date.now()), 200);
		return () => clearInterval(timer);
	}, [donations, now]);

	const arrived = donations.filter((d) => d.timestamp <= now).length;
	const allDone = arrived === donations.length;

	return (
		<>
			<div className="flex items-center gap-2">
				{donations.map((d, i) => (
					<div
						key={`${i}-${d.timestamp}`}
						className={`h-2 w-2 rounded-full transition-colors ${d.timestamp <= now ? 'bg-primary' : 'bg-gray-300'}`}
					/>
				))}
			</div>
			<p className="text-sm text-gray-500">
				{arrived === 0
					? 'Waiting for donations...'
					: allDone
						? `All ${donations.length} donations received`
						: `${arrived}/${donations.length} donations received`}
			</p>
		</>
	);
}

// ---------------------------------------------------------------------------
// JSON Data Display
// ---------------------------------------------------------------------------

function DonationJsonDisplay({
	label,
	donations,
	totalAtWindowStartCents,
	active,
}: {
	label: string;
	donations: Donation[];
	totalAtWindowStartCents: number;
	active?: boolean;
}) {
	const windowDonationsCents = donations.reduce((sum, d) => sum + d.amountCents, 0);

	return (
		<div className={`rounded-lg p-4 ${active ? 'bg-primary/5 ring-primary ring-2' : 'bg-gray-50'}`}>
			<h4 className="mb-2 text-sm font-semibold text-gray-700">{label}</h4>
			<pre className="max-h-64 overflow-auto rounded bg-gray-900 p-4 text-xs text-green-400">
				{JSON.stringify(
					{
						totalAtWindowStartCents,
						totalAtWindowStartEuros: (totalAtWindowStartCents / 100).toFixed(2),
						donations: donations.map((d) => ({
							amountCents: d.amountCents,
							amountEuros: (d.amountCents / 100).toFixed(2),
							timestamp: new Date(d.timestamp).toISOString(),
						})),
						windowDonationsEuros: (windowDonationsCents / 100).toFixed(2),
						finalTotal: ((totalAtWindowStartCents + windowDonationsCents) / 100).toFixed(2),
					},
					null,
					2,
				)}
			</pre>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main page — uses TanStack Query just like the production index.tsx
// ---------------------------------------------------------------------------

function Component() {
	// Track the current window key — mirrors index.tsx with currentWindowKey()
	const [windowStartMs, setWindowStartMs] = useState(currentDemoWindowKey);
	useEffect(() => {
		const timer = setInterval(() => {
			const key = currentDemoWindowKey();
			if (key !== windowStartMs) setWindowStartMs(key);
		}, 200);
		return () => clearInterval(timer);
	}, [windowStartMs]);

	const hourStart = Math.floor(windowStartMs / (60 * 60_000)) * (60 * 60_000);
	const windowIndex = Math.round((windowStartMs - hourStart) / DEMO_WINDOW_MS);

	// Static fallback mirrors production: shows a fixed value while suspense resolves
	const staticFallback = `${(BASE_TOTAL_CENTS / 100).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`;

	return (
		<Layout>
			<Header />

			<section className="mx-auto flex max-w-5xl flex-col gap-12 p-8 py-16">
				<div>
					<h1 className="text-4xl font-bold">Donation Counter Demo</h1>
					<p className="mt-2 text-gray-600">
						Mirrors the production implementation using TanStack Query. The key changes every {DEMO_WINDOW_MS / 1000}s
						(simulating 5 min), and the fake server returns ~{((DEMO_WINDOW_MS * 6) / 5 / 1000).toFixed(1)}s of data
						(simulating 6 min) for a 1-min overlap margin.
					</p>
					<p className="mt-1 text-xs text-gray-400">
						Window key: {new Date(windowStartMs).toISOString()} | Index in hour: {windowIndex}
					</p>
				</div>

				<div>
					<h2 className="mb-4 text-2xl font-semibold">Window {windowIndex + 1}</h2>

					<div className="rounded-lg border p-6">
						<h3 className="text-primary text-center text-4xl font-bold">
							<Suspense fallback={<>{staticFallback}</>}>
								<SuspensedTotalRaised windowStartMs={windowStartMs} />
							</Suspense>
						</h3>
						<div className="mt-4 flex flex-col items-center gap-2">
							<Suspense>
								<SuspensedDonationDots windowStartMs={windowStartMs} />
							</Suspense>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					<Suspense>
						<SuspensedJsonDisplays windowStartMs={windowStartMs} windowIndex={windowIndex} />
					</Suspense>
				</div>
			</section>

			<Footer />
		</Layout>
	);
}

function SuspensedTotalRaised({ windowStartMs }: { windowStartMs: number }) {
	const { language } = useTranslation();
	const { data } = useSuspenseQuery(demoRecentDonationsOptions(windowStartMs));

	return (
		<AnimatedTotalRaised
			totalAtWindowStartCents={data.totalAtWindowStartCents}
			donations={data.donations}
			language={language}
		/>
	);
}

function SuspensedDonationDots({ windowStartMs }: { windowStartMs: number }) {
	const { data } = useSuspenseQuery(demoRecentDonationsOptions(windowStartMs));
	return <DonationProgressDots donations={data.donations} />;
}

function SuspensedJsonDisplays({ windowStartMs, windowIndex }: { windowStartMs: number; windowIndex: number }) {
	const { data: currentData } = useSuspenseQuery(demoRecentDonationsOptions(windowStartMs));

	// Query next window for debug visibility
	const nextWindowStartMs = windowStartMs + DEMO_WINDOW_MS;
	const { data: nextData } = useQuery(demoRecentDonationsOptions(nextWindowStartMs));

	const nextTotalAtWindowStartCents = nextData?.totalAtWindowStartCents ?? currentData.totalAtWindowStartCents;
	const nextDonations = nextData?.donations ?? [];

	return (
		<>
			<DonationJsonDisplay
				label={`Current (window ${windowIndex + 1})`}
				donations={currentData.donations}
				totalAtWindowStartCents={currentData.totalAtWindowStartCents}
				active
			/>
			<DonationJsonDisplay
				label={`Next (window ${windowIndex + 2})`}
				donations={nextDonations}
				totalAtWindowStartCents={nextTotalAtWindowStartCents}
			/>
		</>
	);
}
